/**
 * OrderDetailSidebar — Right-column cards for the Shopify-style two-column
 * order detail layout. Shows COMPLEMENTARY data that the left column doesn't:
 * contact info, shipping address, billing address.
 *
 * Uses the same CARD design token as the left-column ExperienceView widgets
 * for visual consistency.
 */

import { useState, useRef, useEffect } from "react";
import { ChevronDown, Pencil, Check, X, Loader2, Info, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { CARD } from "@/config/design-tokens";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation, getSectionTitleTranslation } from "@/lib/views/labels/translations";
import { frappeMethod } from "@/lib/erp/utils";
import { PaymentSlipsCard } from "./PaymentSlipsCard";
import { ShopeeOrderBanner } from "./ShopeeOrderBanner";
import { TrackingCard } from "./components/TrackingCard";
import { UserAvatar } from "@/features/common/components/UserAvatar";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OrderDetailSidebarProps {
  formData: Record<string, any>;
  onFieldChange?: (fieldname: string, value: any) => void;
}

// ---------------------------------------------------------------------------
// Shared label + value typography (matches CustomerSection left-column style)
// ---------------------------------------------------------------------------

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
      {children}
    </div>
  );
}

function FieldValue({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 break-words leading-relaxed">
      {children}
    </div>
  );
}

function EmptyValue() {
  return <span className="text-gray-400 dark:text-gray-500 italic">—</span>;
}

// ---------------------------------------------------------------------------
// Editable Field — click to edit inline, Enter to commit
// ---------------------------------------------------------------------------

function EditableField({
  label,
  value,
  fieldname,
  editing,
  draft,
  onDraftChange,
  onFieldChange,
}: {
  label: string;
  value: string;
  fieldname: string;
  editing: boolean;
  draft: Record<string, string>;
  onDraftChange: (fieldname: string, value: string) => void;
  onFieldChange?: (fieldname: string, value: any) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  if (!editing) {
    return (
      <div className="flex flex-col min-w-0">
        <FieldLabel>{label}</FieldLabel>
        <FieldValue>{value || <EmptyValue />}</FieldValue>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-w-0">
      <FieldLabel>{label}</FieldLabel>
      <input
        ref={inputRef}
        type="text"
        value={draft[fieldname] ?? value ?? ""}
        onChange={(e) => onDraftChange(fieldname, e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            onFieldChange?.(fieldname, draft[fieldname] ?? value);
          }
        }}
        className="text-sm font-semibold text-gray-900 dark:text-gray-100 bg-muted/50 border border-border rounded-md px-2 py-1 w-full outline-none focus:ring-1 focus:ring-ring"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contact Card — contact person, mobile, phone, email (inline editable)
// ---------------------------------------------------------------------------

function ContactCard({
  formData,
  onFieldChange,
  salesOrderName,
}: {
  formData: Record<string, any>;
  onFieldChange?: (fieldname: string, value: any) => void;
  salesOrderName: string;
}) {
  const { language } = useLanguage();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  // Local overrides from last save — shown immediately without waiting for parent re-render
  const [saved, setSaved] = useState<Record<string, string>>({});

  const contact = saved.contact_display ?? (formData.contact_display || formData.contact_person || "");
  const mobile = saved.contact_mobile ?? (formData.contact_mobile || "");
  const phone = saved.contact_phone ?? (formData.contact_phone || "");
  const email = saved.contact_email ?? (formData.contact_email || "");

  const g = (key: string, fb: string) => getUITranslation(key, language) || fb;
  const s = (label: string) => getSectionTitleTranslation(label, language);
  const hasData = !!(contact || mobile || phone || email);

  const handleEdit = () => {
    setDraft({
      contact_display: contact,
      contact_mobile: mobile,
      contact_phone: phone,
      contact_email: email,
    });
    setEditing(true);
  };

  const handleSave = async () => {
    // Build dict of only changed fields
    const changes: Record<string, string> = {};
    for (const [key, val] of Object.entries(draft)) {
      const original = formData[key] || "";
      if (val !== original) {
        changes[key] = val;
      }
    }
    if (Object.keys(changes).length === 0) {
      setEditing(false);
      setDraft({});
      return;
    }

    setSaving(true);
    try {
      await frappeMethod("zaviago_backend.api.doc_sidebar.update_sidebar_fields", {
        doctype: "Sales Order",
        name: salesOrderName,
        fields: changes,
      });
      // Show saved values immediately (local override)
      setSaved((prev) => ({ ...prev, ...changes }));
      // Also update parent state for other consumers
      for (const [key, val] of Object.entries(changes)) {
        onFieldChange?.(key, val);
      }
      setEditing(false);
      setDraft({});
    } catch {
      // Keep editing mode open so user can retry
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setDraft({});
  };

  const handleDraftChange = (fieldname: string, value: string) => {
    setDraft((prev) => ({ ...prev, [fieldname]: value }));
  };

  const contactFields = [
    { label: g("contact", "Contact"), value: contact, fieldname: "contact_display" },
    { label: g("mobile", "Mobile"), value: mobile, fieldname: "contact_mobile" },
    { label: g("phone", "Phone"), value: phone, fieldname: "contact_phone" },
    { label: g("email", "Email"), value: email, fieldname: "contact_email" },
  ];

  // In view mode, only show fields with values
  const visibleFields = editing ? contactFields : contactFields.filter((f) => f.value);

  return (
    <div className={CARD}>
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            {s("Contact")}
          </span>
          {editing ? (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1 text-[11px] font-medium text-green-600 hover:text-green-700 transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                {g("save", "Save")}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors ml-2"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleEdit}
              className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <Pencil className="h-3 w-3" />
              {g("edit", "Edit")}
            </button>
          )}
        </div>
        {hasData || editing ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            {visibleFields.map((f) => (
              <EditableField
                key={f.fieldname}
                label={f.label}
                value={f.value}
                fieldname={f.fieldname}
                editing={editing}
                draft={draft}
                onDraftChange={handleDraftChange}
                onFieldChange={onFieldChange}
              />
            ))}
          </div>
        ) : (
          <EmptyValue />
        )}
        <ShopeeDataCensorTip formData={formData} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shopee censored-data info tip
// ---------------------------------------------------------------------------

const MARKETPLACE_CHANNELS = ["Shopee", "Lazada", "TikTok Shop"];

function ShopeeDataCensorTip({
  formData,
}: {
  formData: Record<string, any>;
}) {
  const channel = (formData.source_channel as string) || "";
  if (!MARKETPLACE_CHANNELS.includes(channel)) return null;

  // Detect if any visible data looks censored (contains *)
  const fields = [formData.contact_display, formData.contact_person, formData.customer_name];
  const hasCensored = fields.some((v) => typeof v === "string" && v.includes("*"));

  const isShopee = channel === "Shopee";

  // Determine which Shopee action is available based on order status
  const shopeeStatus = ((formData.shopee_order_status as string) || "").toUpperCase();
  const canShip = shopeeStatus === "READY_TO_SHIP";
  const canReprint = ["SHIPPED", "PROCESSED"].includes(shopeeStatus);
  const isExpired = ["COMPLETED", "TO_RETURN"].includes(shopeeStatus);

  // Build the dynamic button label
  const actionLabel = canShip
    ? `"Ship Order"`
    : canReprint
    ? `"Reprint Label"`
    : `"Ship Order" หรือ "Reprint Label"`;

  return (
    <div className={cn(
      "mt-3 flex gap-2 rounded-lg px-3 py-2.5 border",
      hasCensored && isShopee && isExpired
        ? "bg-amber-50/60 dark:bg-amber-950/30 border-amber-100 dark:border-amber-900/50"
        : "bg-blue-50/60 dark:bg-blue-950/30 border-blue-100 dark:border-blue-900/50",
    )}>
      <Info className={cn(
        "h-3.5 w-3.5 shrink-0 mt-0.5",
        hasCensored && isShopee && isExpired ? "text-amber-500" : "text-blue-500",
      )} />
      <div className={cn(
        "text-[11px] leading-relaxed",
        hasCensored && isShopee && isExpired
          ? "text-amber-700 dark:text-amber-300"
          : "text-blue-700 dark:text-blue-300",
      )}>
        {hasCensored ? (
          isShopee ? (
            isExpired ? (
              <>
                {channel} ปกปิดข้อมูลผู้ซื้อไว้ — การเปิดเผยอัตโนมัติทำได้เฉพาะสถานะ
                <span className="font-medium"> Shipped </span>
                เท่านั้น กด "Edit" เพื่อแก้ไขด้วยตนเอง
              </>
            ) : (
              <>
                {channel} ปกปิดข้อมูลผู้ซื้อไว้ — กด
                <span className="font-medium"> {actionLabel} </span>
                ระบบจะเปิดเผยชื่อจริงและอัปเดตให้อัตโนมัติ
              </>
            )
          ) : (
            <>
              {channel} ปกปิดข้อมูลผู้ซื้อไว้ — กด "Edit" เพื่อแก้ไขด้วยตนเอง
            </>
          )
        ) : isShopee ? (
          <>
            หากข้อมูลไม่ตรง กด
            <span className="font-medium"> {canReprint ? `"Reprint Label"` : `"Ship Order"`} </span>
            ระบบจะดึงข้อมูลจริงมาอัปเดตให้
          </>
        ) : (
          <>
            ข้อมูลจาก {channel} — กด "Edit" เพื่อแก้ไข
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Address helpers
// ---------------------------------------------------------------------------

/** Parse `<br>`-separated HTML address into lines */
function parseAddressLines(html?: string | null): string[] {
  if (!html) return [];
  // Strip tags except <br>, then split on <br> variants
  const text = html.replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]+>/g, "");
  return text.split("\n").map((l) => l.trim()).filter(Boolean);
}

/** Join lines back into `<br>` HTML */
function joinAddressLines(lines: string[]): string {
  return lines.filter(Boolean).join("<br>");
}

function AddressDisplay({ html }: { html?: string | null }) {
  if (!html) return <FieldValue><EmptyValue /></FieldValue>;
  return (
    <div
      className="text-sm font-medium text-foreground leading-relaxed"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// ---------------------------------------------------------------------------
// Editable Address Section — one address block with inline line editing
// ---------------------------------------------------------------------------

function EditableAddressSection({
  title,
  html,
  fieldname,
  salesOrderName,
  onFieldChange,
}: {
  title: string;
  html?: string | null;
  fieldname: string;
  salesOrderName: string;
  onFieldChange?: (fieldname: string, value: any) => void;
}) {
  const { language } = useLanguage();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lines, setLines] = useState<string[]>([]);
  const [savedHtml, setSavedHtml] = useState<string | null>(null);

  const g = (key: string, fb: string) => getUITranslation(key, language) || fb;
  const displayHtml = savedHtml ?? html;

  const handleEdit = () => {
    const parsed = parseAddressLines(displayHtml);
    // Ensure at least 4 lines for: line1, line2, city/state/zip, country
    while (parsed.length < 4) parsed.push("");
    setLines(parsed);
    setEditing(true);
  };

  const handleSave = async () => {
    const newHtml = joinAddressLines(lines);
    if (newHtml === (displayHtml || "")) {
      setEditing(false);
      return;
    }

    // For unsaved new orders, just update local form state (no SO to persist to yet)
    if (!salesOrderName) {
      setSavedHtml(newHtml);
      onFieldChange?.(fieldname, newHtml);
      setEditing(false);
      return;
    }

    setSaving(true);
    try {
      await frappeMethod("zaviago_backend.api.doc_sidebar.update_sidebar_fields", {
        doctype: "Sales Order",
        name: salesOrderName,
        fields: { [fieldname]: newHtml },
      });
      setSavedHtml(newHtml);
      onFieldChange?.(fieldname, newHtml);
      setEditing(false);
    } catch {
      // Keep editing open on error
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setLines([]);
  };

  const updateLine = (idx: number, value: string) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? value : l)));
  };

  const addLine = () => setLines((prev) => [...prev, ""]);

  return (
    <div className="p-5">
      <div className="flex items-center justify-between mb-4">
        <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          {title}
        </span>
        {editing ? (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1 text-[11px] font-medium text-green-600 hover:text-green-700 transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              {g("save", "Save")}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors ml-2"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleEdit}
            className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <Pencil className="h-3 w-3" />
            {g("edit", "Edit")}
          </button>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          {lines.map((line, idx) => (
            <input
              key={idx}
              type="text"
              value={line}
              onChange={(e) => updateLine(idx, e.target.value)}
              placeholder={idx === 0 ? "Address line 1" : idx === 1 ? "Address line 2" : idx === 2 ? "City, State ZIP" : "Country"}
              className="text-sm font-semibold text-gray-900 dark:text-gray-100 bg-muted/50 border border-border rounded-md px-2 py-1 w-full outline-none focus:ring-1 focus:ring-ring"
            />
          ))}
          <button
            type="button"
            onClick={addLine}
            className="text-[11px] text-muted-foreground hover:text-foreground transition-colors"
          >
            + {g("add_line", "Add line")}
          </button>
        </div>
      ) : (
        <AddressDisplay html={displayHtml} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Combined Address Card — shipping always visible, billing collapsible
// ---------------------------------------------------------------------------

export function AddressesCard({
  formData,
  onFieldChange,
  salesOrderName,
}: {
  formData: Record<string, any>;
  onFieldChange?: (fieldname: string, value: any) => void;
  salesOrderName: string;
}) {
  const { language } = useLanguage();
  const [billingOpen, setBillingOpen] = useState(false);
  const shippingHtml = formData.shipping_address;
  const billingHtml = formData.address_display;

  const s = (label: string) => getSectionTitleTranslation(label, language);

  return (
    <div className={CARD}>
      <EditableAddressSection
        title={s("Shipping Address")}
        html={shippingHtml}
        fieldname="shipping_address"
        salesOrderName={salesOrderName}
        onFieldChange={onFieldChange}
      />
      {/* Billing Address — collapsible */}
      {billingHtml && (
        <>
          <div className="mx-5 border-t border-border/40" />
          <button
            type="button"
            onClick={() => setBillingOpen((v) => !v)}
            className="w-full flex items-center justify-between px-5 py-3 hover:bg-muted/30 transition-colors"
          >
            <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {s("Billing Address")}
            </span>
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 text-muted-foreground transition-transform duration-200",
                billingOpen && "rotate-180",
              )}
            />
          </button>
          {billingOpen && (
            <EditableAddressSection
              title={s("Billing Address")}
              html={billingHtml}
              fieldname="address_display"
              salesOrderName={salesOrderName}
              onFieldChange={onFieldChange}
            />
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sales Team Card — compact sidebar view of assigned sales persons
// ---------------------------------------------------------------------------

function SalesTeamCard({
  formData,
  onFieldChange,
  salesOrderName,
}: {
  formData: Record<string, any>;
  onFieldChange?: (fieldname: string, value: any) => void;
  salesOrderName: string;
}) {
  const { language } = useLanguage();
  const s = (label: string) => getSectionTitleTranslation(label, language);
  const g = (key: string, fb: string) => getUITranslation(key, language) || fb;

  const sourceRows: Array<Record<string, any>> = Array.isArray(formData.sales_team)
    ? formData.sales_team
    : [];
  // Local rows for instant UI updates after add/remove
  const [localRows, setLocalRows] = useState<Array<Record<string, any>> | null>(null);
  const rows = localRows ?? sourceRows;

  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<Array<{ name: string; sales_person_name: string }>>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Reset local rows when parent data changes (e.g. after refetch)
  useEffect(() => {
    setLocalRows(null);
  }, [sourceRows.length]);

  const handleSearch = (txt: string) => {
    setSearch(txt);
    clearTimeout(debounceRef.current);
    if (!txt.trim()) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await frappeMethod<Array<{ name: string; sales_person_name: string }>>(
          "zaviago_backend.api.doc_sidebar.search_sales_persons",
          { txt, limit: 8 },
        );
        setResults(res || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const handleAdd = async (spName: string) => {
    setSaving(true);
    try {
      const res = await frappeMethod<{ ok: boolean; row_name: string }>("zaviago_backend.api.doc_sidebar.add_sales_team_member", {
        doctype: "Sales Order",
        name: salesOrderName,
        sales_person: spName,
        allocated_percentage: 100,
      });
      const rowName = res?.row_name || `new-${Date.now()}`;
      setLocalRows([...rows, { sales_person: spName, allocated_percentage: 100, name: rowName }]);
      onFieldChange?.("sales_team", [...rows, { sales_person: spName, allocated_percentage: 100 }]);
      setSearch("");
      setResults([]);
      setAdding(false);
    } catch {
      // Keep search open on error
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (rowName: string) => {
    setSaving(true);
    try {
      await frappeMethod("zaviago_backend.api.doc_sidebar.remove_sales_team_member", {
        doctype: "Sales Order",
        name: salesOrderName,
        row_name: rowName,
      });
      const updated = rows.filter((r) => r.name !== rowName);
      setLocalRows(updated);
      onFieldChange?.("sales_team", updated);
    } catch {
      // Silently fail — row stays visible
    } finally {
      setSaving(false);
    }
  };

  const handleStartAdd = () => {
    setAdding(true);
    setSearch("");
    setResults([]);
    setTimeout(() => searchRef.current?.focus(), 50);
  };

  return (
    <div className={CARD}>
      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            {s("Sales Team")}
          </span>
          {!adding && (
            <button
              type="button"
              onClick={handleStartAdd}
              className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <Plus className="h-3 w-3" />
              {g("add", "Add")}
            </button>
          )}
        </div>

        {rows.length === 0 && !adding ? (
          <EmptyValue />
        ) : (
          <div className="space-y-2.5">
            {rows.map((r, idx) => {
              const spName = r.sales_person || "—";
              const pct = r.allocated_percentage;
              return (
                <div key={r.name || idx} className="flex items-center gap-3 min-w-0 group">
                  <UserAvatar fallbackText={spName} size="sm" className="shrink-0" />
                  <span className="text-sm font-medium text-foreground truncate flex-1 min-w-0">
                    {spName}
                  </span>
                  {pct != null && pct !== "" && (
                    <span className="text-[11px] font-medium text-muted-foreground bg-muted/60 rounded-full px-2 py-0.5 shrink-0">
                      {pct}%
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemove(r.name)}
                    disabled={saving}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-red-500 shrink-0 disabled:opacity-50"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {adding && (
          <div className="mt-3">
            <div className="relative">
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder={g("search_sales_person", "Search sales person...")}
                className="text-sm text-foreground bg-muted/50 border border-border rounded-md px-2 py-1.5 w-full outline-none focus:ring-1 focus:ring-ring"
              />
              {searching && <Loader2 className="absolute right-2 top-2 h-3.5 w-3.5 animate-spin text-muted-foreground" />}
            </div>
            {results.length > 0 && (
              <div className="mt-1 border border-border rounded-md bg-popover shadow-sm max-h-32 overflow-y-auto">
                {results.map((sp) => (
                  <button
                    key={sp.name}
                    type="button"
                    onClick={() => handleAdd(sp.name)}
                    disabled={saving}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition-colors disabled:opacity-50"
                  >
                    {sp.sales_person_name || sp.name}
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => { setAdding(false); setSearch(""); setResults([]); }}
              className="mt-2 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {g("cancel", "Cancel")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export function OrderDetailSidebar({ formData, onFieldChange }: OrderDetailSidebarProps) {
  const salesOrderName = (formData.name as string) || "";
  const paymentMethodType = (formData.payment_method_type as string) || "";
  const isStorefront = typeof formData.source_channel === "string" && formData.source_channel.startsWith("Storefront");
  const orderStatus = (formData.status as string) || "";

  return (
    <div className="space-y-4">
      <ShopeeOrderBanner formData={formData} salesOrderName={salesOrderName} />
      <ContactCard formData={formData} onFieldChange={onFieldChange} salesOrderName={salesOrderName} />
      <PaymentSlipsCard
        salesOrderName={salesOrderName}
        paymentMethodType={paymentMethodType}
        isStorefront={isStorefront}
        orderStatus={orderStatus}
        customerName={(formData.customer_name as string) || ""}
      />
      <AddressesCard formData={formData} onFieldChange={onFieldChange} salesOrderName={salesOrderName} />
      <TrackingCard
        salesOrderName={salesOrderName}
        isMarketplace={!!(formData.shopee_order_sn || formData.lazada_order_status || formData.tiktok_order_id)}
      />
      <SalesTeamCard formData={formData} onFieldChange={onFieldChange} salesOrderName={salesOrderName} />
    </div>
  );
}
